<script lang="ts">
import ProductCheckoutForm from '@/components/forms/ProductCheckoutForm.vue'
import { useTailwind } from '@/plugins/useTailwind'
import type { CheckoutContext, InstantWinDraw, Membership, Plan } from '@/types'
import { ProductTypes } from '@/utils/helper-objects'
import { isE164PhoneNumber, isPassword } from '@/utils/input-validator-utils'
import { type PropType, defineComponent } from 'vue'

export default defineComponent({
  components: {
    ProductCheckoutForm,
  },
  props: {
    checkoutContext: {
      type: Object as PropType<CheckoutContext>,
      required: true,
    },
    isLoadingCredit: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const tailwind = useTailwind()

    return {
      isLg: tailwind.isLg,
    }
  },
  data: () => ({
    form: {
      isLoading: false,
      data: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        rememberMe: true,
      },
      errors: null as any,
      validators: {
        password: isPassword,
        phone: [isE164PhoneNumber],
      },
    },
    cachedForm: null as any,
    checkoutDataIsReady: false,
    closeModalFunction: null as (() => void) | null,
    paymentIsProcessing: false,
  }),
  computed: {
    isDisabled(): boolean {
      // Check if any instant wins in basket are sold out
      const instantWinItems = this.checkoutContext.basket.items.filter(
        (item) => item.type === ProductTypes.InstantWinDraw
      )
      return instantWinItems.some((item) => {
        const instantWin = item.product as InstantWinDraw
        return instantWin.total_purchased_tickets >= instantWin.total_tickets
      })
    },
    buttonText(): string {
      if (this.checkoutContext.basket.items.length === 0) {
        return 'Add Items'
      }

      const hasSubscription = this.checkoutContext.basket.items.some(
        (item) => item.type === ProductTypes.Subscription
      )
      const hasInstantWin = this.checkoutContext.basket.items.some(
        (item) => item.type === ProductTypes.InstantWinDraw
      )

      if (hasSubscription && hasInstantWin) {
        return 'Complete Purchase'
      } else if (hasSubscription) {
        return 'Join Plan'
      } else if (hasInstantWin) {
        return 'Buy Entries'
      } else {
        return 'Complete Purchase'
      }
    },
  },
  watch: {
    selectedAction: {
      handler(newValue) {
        this.form.errors = null

        if (newValue === 'login') {
          this.form.data.first_name = ''
          this.form.data.last_name = ''
          this.form.data.phone = ''
          this.form.data.rememberMe = true
          this.form.validators = {
            password: () => true,
            phone: [() => true],
          }
        } else {
          this.form.validators = {
            password: isPassword,
            phone: [isE164PhoneNumber],
          }
        }
      },
    },
    form: {
      handler(newValue) {
        this.cachedForm = newValue
      },
      deep: true,
    },
    isLg(newVal, oldVal) {
      if (this.cachedForm && oldVal !== newVal) {
        // Reset form to cachedForm when switching between mobile and desktop
        this.form = JSON.parse(JSON.stringify(this.cachedForm))
      }
    },
  },
  methods: {
    handleLogin() {
      // Check if user already has this plan
      if (this.checkIfUserAlreadyHasPlan()) {
        return
      }

      // Set checkout data ready immediately and ensure reactivity
      void this.$nextTick(() => {
        this.checkoutDataIsReady = true
      })
    },
    handleRegister() {
      // Check if user already has this plan (shouldn't happen for new users, but just in case)
      if (this.checkIfUserAlreadyHasPlan()) {
        return
      }

      // Set checkout data ready immediately and ensure reactivity
      void this.$nextTick(() => {
        this.checkoutDataIsReady = true
      })
    },
    checkIfUserAlreadyHasPlan(): boolean {
      // Get the plan from the basket
      const subscriptionItems = this.checkoutContext.basket.items.filter(
        (item) => item.type === ProductTypes.Subscription
      )

      if (subscriptionItems.length === 0) {
        return false // No subscription in basket
      }

      const planInBasket = subscriptionItems[0].product as Plan

      // Check if user already has a subscription with the same product code
      const userSubscriptions = this.$userStore.user?.subscriptions || []
      const alreadyHasPlan = userSubscriptions.some(
        (subscription: Membership) => subscription.code === planInBasket.code
      )

      if (alreadyHasPlan) {
        // Show success message and close modal
        this.$alertStore.addSuccessAlert({
          title: 'Already Subscribed!',
          message: `Congratulations! You already have the ${(planInBasket as any).name} subscription.`,
          buttonText: 'Great!',
        })

        // Close the modal
        if (this.closeModalFunction) {
          this.closeModalFunction()
        }
        this.checkoutDataIsReady = false

        return true
      }

      return false
    },
    handleExpiredReservation(closeSheetOrModal: () => void) {
      closeSheetOrModal()
      this.checkoutDataIsReady = false
      this.$alertStore.addErrorAlert({
        title: 'Reservation Expired',
        message: 'Please try again.',
        buttonText: 'Close',
      })
    },
    handleOpenSheetOrModal(openSheetOrModal: () => void) {
      openSheetOrModal()
      this.checkoutDataIsReady = true
    },
    handlePaymentProcessingUpdate(isProcessing: boolean) {
      this.paymentIsProcessing = isProcessing
    },
  },
})
</script>

<template>
  <AppModal
    v-if="isLg"
    id="product-checkout-modal"
    button-class="w-full"
    :allow-dismissal="!paymentIsProcessing"
    :dialog-mode="false"
    @close="checkoutDataIsReady = false"
  >
    <template #button="{ openModal }">
      <slot name="button" :open-modal="() => handleOpenSheetOrModal(openModal)">
        <AppButton
          id="productCheckoutButton"
          class="button-primary mx-auto mt-4 w-full uppercase"
          :title="buttonText"
          :disabled="isDisabled"
          @click="handleOpenSheetOrModal(openModal)"
        >
          {{ buttonText }}
        </AppButton>
      </slot>
    </template>

    <template #default="{ closeModal }">
      <!-- Store the close function reference -->
      {{ (closeModalFunction = closeModal) && '' }}

      <!-- Loading overlay for credit balance refresh -->
      <div
        v-if="isLoadingCredit"
        class="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-black/50"
      >
        <div class="rounded-lg bg-white p-6 text-center shadow-lg">
          <div class="spinner mx-auto mb-4"></div>
          <p class="font-medium text-gray-700">Fetching your balance...</p>
          <p class="mt-2 text-sm text-gray-500">This may take a moment</p>
        </div>
      </div>

      <ProductCheckoutForm
        :checkout-context="checkoutContext"
        :checkout-data-is-ready="checkoutDataIsReady"
        @submit-login="handleLogin"
        @submit-register="handleRegister"
        @reservation-expired="handleExpiredReservation(closeModal)"
        @payment-processing-update="handlePaymentProcessingUpdate"
      />
    </template>
  </AppModal>

  <AppDrawer
    v-else
    id="product-checkout-sheet"
    position="bottom"
    :allow-dismissal="!paymentIsProcessing"
    @close="checkoutDataIsReady = false"
  >
    <template #button="{ openDrawer }">
      <slot
        name="button"
        :open-modal="() => handleOpenSheetOrModal(openDrawer)"
      >
        <AppButton
          id="productCheckoutButton"
          class="button-primary mx-auto mt-4 w-full uppercase"
          :title="buttonText"
          :disabled="isDisabled"
          @click="handleOpenSheetOrModal(openDrawer)"
        >
          {{ buttonText }}
        </AppButton>
      </slot>
    </template>

    <template #default="{ closeDrawer }">
      <!-- Store the close function reference -->
      {{ (closeModalFunction = closeDrawer) && '' }}

      <!-- Loading overlay for credit balance refresh -->
      <div
        v-if="isLoadingCredit"
        class="absolute inset-0 z-50 flex items-center justify-center bg-black/50"
      >
        <div class="mx-4 rounded-lg bg-white p-6 text-center shadow-lg">
          <div class="spinner mx-auto mb-4"></div>
          <p class="font-medium text-gray-700">Fetching your balance...</p>
          <p class="mt-2 text-sm text-gray-500">This may take a moment</p>
        </div>
      </div>

      <ProductCheckoutForm
        :checkout-context="checkoutContext"
        :checkout-data-is-ready="checkoutDataIsReady"
        @submit-login="handleLogin"
        @submit-register="handleRegister"
        @reservation-expired="handleExpiredReservation(closeDrawer)"
        @payment-processing-update="handlePaymentProcessingUpdate"
      />
    </template>
  </AppDrawer>
</template>
